import {
  Datagrid,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  TextInput,
} from 'react-admin';
import { Alert, Avatar, Box, Chip, Typography } from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

import { displayNameFor, monoFieldSx, semanticStateColor } from '@src/components/indexPage';

import { formatMaskedNumber, formatMoney } from './format';
import GiftcardExpand from './GiftcardExpand';
import { associatedModel, deliveryAddressOf, issuedDate, modelType } from './GiftcardDetailSections';
import GiftcardListError from './GiftcardListError';
import { useGiftcardConstants } from './useGiftcardConstants';
import type { ConstantOption, GiftcardRecord } from './types';

const toChoices = (options: ConstantOption[]) =>
  options.map(({ code, displayName }) => ({ id: code, name: displayName }));

const GiftcardList = () => {
  const { states, sources, reasons, isFallback } = useGiftcardConstants();

  const filters = [
    // A TextInput, not a SearchInput: the API matches `number` by equality against the full,
    // unmasked value, so the magnifying-glass affordance promised a fuzzy search that does not
    // exist — and the masked digits shown in the list can never match. Every other gift card
    // filter is exact-match too; this resource has no free-text search.
    <TextInput
      key="giftcardNumber"
      source="giftcardNumber"
      label="Gift card number"
      placeholder="Full number, exact match"
      alwaysOn
    />,
    <SelectInput key="state" source="state" label="State" choices={toChoices(states)} />,
    <SelectInput key="source" source="source" label="Source" choices={toChoices(sources)} />,
    <SelectInput key="reason" source="reason" label="Reason" choices={toChoices(reasons)} />,
    <TextInput key="email" source="email" label="Email" />,
    <TextInput key="lastName" source="lastName" label="Last name" />,
    <TextInput key="firstName" source="firstName" label="First name" />,
    // min 0.01: the API discards a zero amount filter, so accepting one would show an active
    // chip over an unfiltered list.
    <NumberInput key="amount" source="amount" label="Amount (USD)" min={0.01} step={0.01} />,
    <TextInput key="orderNumber" source="orderNumber" label="Order number" />,
    <TextInput key="tradeinNumber" source="tradeinNumber" label="Trade-in number" />,
  ];

  return (
    <>
      {isFallback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Showing default filter options — live gift card states, sources, and reasons
          couldn&apos;t be loaded, so filtering may be incomplete.
        </Alert>
      )}
      {/* No `sort` prop, and every column is sortable={false}: the reware-api
          `giftcards` query takes no sort argument and always returns id DESC,
          so any sort affordance would flip the arrow and change nothing. */}
      {/* Without an `error` node react-admin renders the children anyway, so a failed query
          shows an empty datagrid behind a toast — indistinguishable from a partner with no
          gift cards. */}
      <List
        resource="giftcards"
        title="Gift cards"
        filters={filters}
        perPage={20}
        exporter={false}
        error={<GiftcardListError />}
      >
        <Datagrid
          bulkActionButtons={false}
          expand={<GiftcardExpand states={states} sources={sources} reasons={reasons} />}
          rowClick="expand"
          sx={{ '& .RaDatagrid-rowCell': { verticalAlign: 'middle' } }}
        >
          <FunctionField<GiftcardRecord>
            source="number"
            label="Gift card"
            sortable={false}
            render={gc => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  variant="rounded"
                  sx={{ width: 40, height: 40, bgcolor: 'grey.100', color: 'grey.500', border: '1px solid', borderColor: 'divider' }}
                >
                  <CardGiftcardIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: monoFieldSx.fontFamily }}>
                    {formatMaskedNumber(gc.number)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {[`#${gc.id}`, modelType(gc), associatedModel(gc)?.uuidPrefix]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </Box>
            )}
          />
          <FunctionField<GiftcardRecord>
            label="Customer"
            sortable={false}
            render={gc => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {deliveryAddressOf(gc)?.name ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {deliveryAddressOf(gc)?.email ?? ''}
                </Typography>
              </Box>
            )}
          />
          <FunctionField<GiftcardRecord>
            label="State"
            sortable={false}
            render={gc => (
              <Chip size="small" color={semanticStateColor(gc.state)} label={displayNameFor(states, gc.state)} />
            )}
          />
          <FunctionField<GiftcardRecord>
            label="Source"
            sortable={false}
            render={gc => displayNameFor(sources, gc.source)}
          />
          <FunctionField<GiftcardRecord>
            label="Amount"
            sortable={false}
            textAlign="right"
            render={gc => (
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  textDecoration: ['CANCELED', 'CANCELED_IN_STORE'].includes(gc.state) ? 'line-through' : 'none',
                  color: ['CANCELED', 'CANCELED_IN_STORE'].includes(gc.state) ? 'text.disabled' : 'text.primary',
                }}
              >
                {formatMoney(gc.amount, gc.transactionCurrencyCode)}
              </Typography>
            )}
          />
          <FunctionField<GiftcardRecord>
            label="Remaining"
            sortable={false}
            textAlign="right"
            render={gc => (
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(gc.balanceAmount, gc.transactionCurrencyCode)}
              </Typography>
            )}
          />
          <FunctionField<GiftcardRecord>
            label="Issued"
            sortable={false}
            source="createdAt"
            render={gc => issuedDate(gc)}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default GiftcardList;
